import React, { createContext, useContext, useMemo } from 'react'
import { Ability, AbilityBuilder } from '@casl/ability'
import { useAuthStore } from '@core/auth/auth.store'

type Actions = string
type Subjects = string | object

export type AppAbility = Ability<[Actions, Subjects]>

const AbilityContext = createContext<AppAbility | null>(null)

export const AbilityProvider = ({ children }: { children: React.ReactNode }) => {
  const { permissions } = useAuthStore()

  const ability = useMemo(() => {
    const { can, rules } = new AbilityBuilder<AppAbility>(Ability as any)

    if (permissions) {
      Object.entries(permissions).forEach(([resource, permissions]) => {
        // Check each permission type (access, read, write, delete)
        if (permissions.access) can('access', resource)
        if (permissions.read) can('read', resource)
        if (permissions.write) can('write', resource)
        if (permissions.delete) can('delete', resource)

        // Handle custom actions if present
        if (permissions.actions) {
          Object.entries(permissions.actions).forEach(([action, allowed]) => {
            if (allowed) can(action, resource)
          })
        }

        // Handle field-level permissions if present
        if (permissions.fields) {
          Object.entries(permissions.fields).forEach(([field, fieldPerms]) => {
            if (fieldPerms.read) can('read', `${resource}.${field}`)
            if (fieldPerms.write) can('write', `${resource}.${field}`)
          })
        }
      })
    }

    return new Ability(rules as any)
  }, [permissions])

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}

export const useAbility = () => {
  const ability = useContext(AbilityContext)
  if (!ability) throw new Error('useAbility must be used within AbilityProvider')
  return ability
}
